import React, { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Facebook,
  Star,
  Home,
  Instagram,
  Music,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xwvrzgkg";

export const Contact = () => {
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
    source: "contact-section",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Message sent!",
          description:
            "Your message was sent successfully. We will contact you soon.",
        });

        setFormData({
          name: "",
          phone: "",
          email: "",
          service: "",
          message: "",
          source: "contact-section",
        });
      } else {
        toast({
          title: "Submission failed",
          description:
            result?.errors?.[0]?.message ||
            "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Network error",
        description:
          "We could not send your message right now. Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-black text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Contact Us</h2>
          <p className="text-gray-300">
            Get in touch for your free estimate today
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* LEFT INFO */}
          <div className="space-y-8">
            {/* PHONE */}
            <div className="flex items-start gap-4">
              <div className="bg-green-500/15 p-3 rounded-full">
                <Phone className="text-green-400 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Phone</h3>
                <a
                  href="tel:2067080084"
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  (206) 708-0084
                </a>
              </div>
            </div>

            {/* EMAIL */}
            <div className="flex items-start gap-4">
              <div className="bg-green-500/15 p-3 rounded-full">
                <Mail className="text-green-400 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Email</h3>
                <a
                  href="mailto:miguel@samaniegaslandscapingllc.com"
                  className="text-gray-300 hover:text-green-400 transition-colors break-all"
                >
                  miguel@samaniegaslandscapingllc.com
                </a>
              </div>
            </div>

            {/* FACEBOOK */}
            <div className="flex items-start gap-4">
              <div className="bg-blue-500/15 p-3 rounded-full">
                <Facebook className="text-blue-500 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Facebook</h3>
                <a
                  href="https://www.facebook.com/profile.php?id=61555666033245"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-blue-500 text-blue-500 px-4 py-2 rounded-lg hover:bg-blue-500 hover:text-white transition-all"
                >
                  <Facebook className="h-4 w-4" />
                  Samaniega’s Landscaping LLC
                </a>
              </div>
            </div>

            {/* YELP */}
            <div className="flex items-start gap-4">
              <div className="bg-red-500/15 p-3 rounded-full">
                <Star className="text-red-500 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Yelp</h3>
                <a
                  href="https://www.yelp.com/biz/samaniega-s-landscaping-des-moines"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-red-500 text-red-500 px-4 py-2 rounded-lg hover:bg-red-500 hover:text-white transition-all"
                >
                  <Star className="h-4 w-4" />
                  Samaniega's on Yelp
                </a>
              </div>
            </div>

            {/* ANGI */}
            <div className="flex items-start gap-4">
              <div className="bg-red-600/15 p-3 rounded-full">
                <span className="text-red-600 font-bold text-lg leading-none">
                  A
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Angi</h3>
                <a
                  href="https://www.angi.com/companylist/us/wa/seattle/samaniegas-landscaping-llc-reviews-11055307.htm?msockid=22c6f114d5266d5016c4e752d4de6c61"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-red-600 text-red-600 px-4 py-2 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                >
                  <span className="font-bold leading-none">A</span>
                  Samaniega's on Angi
                </a>
              </div>
            </div>

            {/* HOMEADVISOR */}
            <div className="flex items-start gap-4">
              <div className="bg-blue-900/15 p-3 rounded-full">
                <Home className="text-blue-900 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">HomeAdvisor</h3>
                <a
                  href="https://www.homeadvisor.com/rated.samaniegaslandscapingllc.158877815.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-blue-900 text-blue-900 px-4 py-2 rounded-lg hover:bg-blue-900 hover:text-white transition-all"
                >
                  <Home className="h-4 w-4" />
                  Samaniega's on HomeAdvisor
                </a>
              </div>
            </div>

            {/* INSTAGRAM */}
            <div className="flex items-start gap-4">
              <div className="bg-purple-500/15 p-3 rounded-full">
                <Instagram className="text-purple-500 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Instagram</h3>
                <a
                  href="https://www.instagram.com/samaniegaslandscaping/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-purple-500 text-purple-500 px-4 py-2 rounded-lg hover:bg-purple-500 hover:text-white transition-all"
                >
                  <Instagram className="h-4 w-4" />
                  Samaniega's on Instagram
                </a>
              </div>
            </div>

            {/* TIKTOK */}
            <div className="flex items-start gap-4">
              <div className="bg-white/10 p-3 rounded-full">
                <Music className="text-white h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">TikTok</h3>
                <a
                  href="https://www.tiktok.com/@jr24598"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 border border-white text-white px-4 py-2 rounded-lg hover:bg-white hover:text-black transition-all"
                >
                  <Music className="h-4 w-4" />
                  Samaniega's on TikTok
                </a>
              </div>
            </div>

            {/* LOCATION */}
            <div className="flex items-start gap-4">
              <div className="bg-green-500/15 p-3 rounded-full">
                <MapPin className="text-green-400 h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Service Area</h3>
                <p className="text-gray-300">
                  Serving Auburn, Kent, Federal Way & Surrounding Areas
                </p>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="bg-white text-black p-6 md:p-8 rounded-2xl shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <input type="hidden" name="source" value={formData.source} />

              <div>
                <Label htmlFor="contact-name">Name *</Label>
                <Input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-phone">Phone *</Label>
                <Input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(206) 555-0123"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-email">Email *</Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-service">Service Needed</Label>
                <Input
                  id="contact-service"
                  name="service"
                  type="text"
                  value={formData.service}
                  onChange={handleChange}
                  placeholder="e.g., Landscaping, Pavers, Artificial Grass"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-500 hover:bg-green-600 text-black font-bold text-lg py-6 rounded-xl"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
